import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaBalanceScale } from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import AuctionComparisonModal from "../../components/marketplace/AuctionComparisonModal";
import ExploreAuctionListingsBrowse from "../../components/marketplace/ExploreAuctionListingsBrowse";
import { buildSearchParams, readParam } from "../../components/marketplace/MarketplaceBrowseUi";
import {
  buildMarketplaceAuctionBrowseHref,
  shouldShowAuctionListings,
} from "../../constants/auctionBrowse";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import {
  getBuyerStartingPrice,
} from "../../utils/auctionPricing";
import {
  findMatchingCategoryIds,
  findMatchingSubCategoryIds,
  matchesListingSearchQuery,
} from "../../utils/listingSearchMatch";
import { MAX_COMPARE_AUCTIONS } from "../../utils/auctionCompare.util";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/product_marketplace.scss";
import "../../assets/styles/auction_marketplace.scss";

const PAGE_SIZE = 12;

const mapCategoryItem = (cat) => ({
  id: cat.category_id ?? cat.id,
  name: cat.category_name || cat.name,
  imagePath: cat.image_path || null,
});

const mapSubCategoryItem = (sub) => ({
  id: sub.sub_category_id ?? sub.id,
  name: sub.sub_category_name || sub.name,
  imagePath: sub.image_path || null,
});

function getProductAttributes(auction) {
  if (!auction?.product_attributes) return {};
  if (typeof auction.product_attributes === "string") {
    try {
      return JSON.parse(auction.product_attributes);
    } catch {
      return {};
    }
  }
  return auction.product_attributes;
}

function getConditionGrade(auction) {
  const attrs = getProductAttributes(auction);
  return (
    attrs.condition_grade ||
    attrs.construction_condition_grade ||
    attrs.packaging_condition_grade ||
    attrs.energy_condition_grade ||
    null
  );
}

function sortAuctions(auctionsList, sortOption) {
  const sorted = [...auctionsList];
  switch (sortOption) {
    case "ending_soon":
      return sorted.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
    case "newest":
      return sorted.sort(
        (a, b) => new Date(b.created_at || b.start_date) - new Date(a.created_at || a.start_date),
      );
    case "price_low":
      return sorted.sort((a, b) => getBuyerStartingPrice(a) - getBuyerStartingPrice(b));
    case "price_high":
      return sorted.sort((a, b) => getBuyerStartingPrice(b) - getBuyerStartingPrice(a));
    default:
      return sorted;
  }
}

export default function AuctionList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allAuctions, setAllAuctions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [browseLoading, setBrowseLoading] = useState(true);

  const applied = useMemo(
    () => ({
      category: readParam(searchParams, "category"),
      subCategory: readParam(searchParams, "subCategory"),
      minPrice: readParam(searchParams, "minPrice"),
      maxPrice: readParam(searchParams, "maxPrice"),
      brand: readParam(searchParams, "brand"),
      country: readParam(searchParams, "country"),
      condition: readParam(searchParams, "condition"),
      year: readParam(searchParams, "year"),
      fuelType: readParam(searchParams, "fuelType"),
      transmission: readParam(searchParams, "transmission"),
      endCondition: readParam(searchParams, "endCondition"),
      sort: readParam(searchParams, "sort", "ending_soon"),
      q: readParam(searchParams, "q") || readParam(searchParams, "keyword"),
      page: readParam(searchParams, "page", "1"),
      limit: readParam(searchParams, "limit", String(PAGE_SIZE)),
    }),
    [searchParams],
  );

  const [draft, setDraft] = useState(applied);
  const [compareIds, setCompareIds] = useState([]);
  const [compareAuctionsById, setCompareAuctionsById] = useState({});
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  const listingsContext = useMemo(
    () => ({
      subCategoriesLoaded: Boolean(applied.category) && !subCategoriesLoading,
      subCategoryCount: subCategories.length,
    }),
    [applied.category, subCategoriesLoading, subCategories.length],
  );
  const showListings = useMemo(
    () => shouldShowAuctionListings(applied, listingsContext),
    [applied, listingsContext],
  );

  useEffect(() => {
    if (showListings) return;
    navigate(
      buildMarketplaceAuctionBrowseHref({
        auctionCategory: applied.category,
        auctionSubCategory: applied.subCategory,
      }),
      { replace: true },
    );
  }, [showListings, applied.category, applied.subCategory, navigate]);
  const pageSize = Math.min(48, Math.max(1, Number(applied.limit) || PAGE_SIZE));
  const currentPage = Math.max(1, Number(applied.page) || 1);

  const categoryTabItems = useMemo(() => categories.map(mapCategoryItem), [categories]);
  const subCategoryTabItems = useMemo(() => subCategories.map(mapSubCategoryItem), [subCategories]);

  const activeCategory = categoryTabItems.find((c) => String(c.id) === String(applied.category));
  const activeSubCategory = subCategoryTabItems.find((c) => String(c.id) === String(applied.subCategory));

  const fetchAuctions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await CommonService.get(API_URLS.AUCTION.ACTIVE);
      if (response?.success && response.data) {
        setAllAuctions(Array.isArray(response.data) ? response.data : []);
      } else {
        setAllAuctions([]);
      }
    } catch (error) {
      console.error("Error fetching auctions:", error);
      toast.error("Failed to fetch auctions");
      setAllAuctions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    setBrowseLoading(true);
    try {
      const response = await CommonService.get(`${API_URLS.AUCTION.BASE}/categories`);
      if (response?.success && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching auction categories:", error);
      setCategories([]);
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuctions();
    fetchCategories();
  }, [fetchAuctions, fetchCategories]);

  useEffect(() => {
    if (!applied.category) {
      setSubCategories([]);
      setSubCategoriesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSubCategoriesLoading(true);
    CommonService.get(`${API_URLS.AUCTION.BASE}/categories/${applied.category}/sub-categories`, {}, { silent: true })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setSubCategories(list);
      })
      .catch(() => {
        if (!cancelled) setSubCategories([]);
      })
      .finally(() => {
        if (!cancelled) setSubCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applied.category]);

  useEffect(() => {
    if (!categories.length) {
      setAllSubCategories([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      categories.map((cat) =>
        CommonService.get(
          `${API_URLS.AUCTION.BASE}/categories/${cat.category_id ?? cat.id}/sub-categories`,
          {},
          { silent: true },
        ),
      ),
    )
      .then((responses) => {
        if (cancelled) return;
        const flat = responses.flatMap((res) => (Array.isArray(res?.data) ? res.data : []));
        setAllSubCategories(flat);
      })
      .catch(() => {
        if (!cancelled) setAllSubCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [categories]);

  const matchingCategoryIds = useMemo(
    () =>
      findMatchingCategoryIds(
        applied.q,
        categoryTabItems.map((cat) => ({ id: cat.id, name: cat.name })),
      ),
    [applied.q, categoryTabItems],
  );

  const matchingSubCategoryIds = useMemo(
    () =>
      findMatchingSubCategoryIds(
        applied.q,
        allSubCategories.map((sub) => ({
          id: sub.sub_category_id ?? sub.id,
          name: sub.sub_category_name || sub.name,
        })),
      ),
    [applied.q, allSubCategories],
  );

  const filterOptions = useMemo(() => {
    const options = {
      brands: new Set(),
      countries: new Set(),
      conditions: new Set(),
      years: new Set(),
      fuelTypes: new Set(),
      transmissions: new Set(),
    };

    let pool = allAuctions;
    if (applied.category) {
      pool = pool.filter((a) => String(a.category_id) === String(applied.category));
    }

    pool.forEach((auction) => {
      const attrs = getProductAttributes(auction);
      const brand = attrs.brand || attrs.model?.split(" ")[0] || auction.product_name?.split(" ")[0];
      if (brand) options.brands.add(brand);
      if (auction.country) options.countries.add(auction.country);
      const condition = getConditionGrade(auction);
      if (condition) options.conditions.add(condition);
      const year =
        attrs.year_of_manufacture ||
        attrs.construction_year_of_manufacture ||
        attrs.packaging_year_of_manufacture ||
        attrs.energy_year_of_manufacture;
      if (year) options.years.add(year);
      const fuelType = attrs.fuel_ev_type || attrs.construction_fuel_type || attrs.fuel_type;
      if (fuelType) options.fuelTypes.add(fuelType);
      if (attrs.transmission) options.transmissions.add(attrs.transmission);
    });

    return {
      brands: Array.from(options.brands).sort(),
      countries: Array.from(options.countries).sort(),
      conditions: Array.from(options.conditions).sort(),
      years: Array.from(options.years).sort((a, b) => String(b).localeCompare(String(a))),
      fuelTypes: Array.from(options.fuelTypes).sort(),
      transmissions: Array.from(options.transmissions).sort(),
    };
  }, [allAuctions, applied.category]);

  const filteredAuctions = useMemo(() => {
    let filtered = [...allAuctions];

    if (applied.category) {
      filtered = filtered.filter((a) => String(a.category_id) === String(applied.category));
    }
    if (applied.subCategory) {
      filtered = filtered.filter((a) => String(a.sub_category_id) === String(applied.subCategory));
    }
    if (applied.q.trim()) {
      filtered = filtered.filter((auction) => {
        const attrs = getProductAttributes(auction);
        const textMatch = matchesListingSearchQuery(applied.q, [
          auction.product_name,
          auction.product_description,
          auction.seller_company,
          auction.seller_name,
          auction.category_name,
          auction.sub_category_name,
          auction.category_description,
          auction.sub_category_description,
          auction.city,
          auction.state,
          auction.country,
          attrs.brand,
          attrs.model,
        ]);
        const categoryMatch = matchingCategoryIds.has(String(auction.category_id));
        const subCategoryMatch = matchingSubCategoryIds.has(String(auction.sub_category_id));
        return textMatch || categoryMatch || subCategoryMatch;
      });
    }
    if (applied.brand) {
      filtered = filtered.filter((a) => {
        const attrs = getProductAttributes(a);
        const brand = attrs.brand || attrs.model?.split(" ")[0] || a.product_name?.split(" ")[0];
        return brand && brand.toLowerCase().includes(applied.brand.toLowerCase());
      });
    }
    if (applied.country) {
      filtered = filtered.filter((a) => a.country === applied.country);
    }
    if (applied.condition) {
      filtered = filtered.filter((a) => getConditionGrade(a) === applied.condition);
    }
    if (applied.year) {
      filtered = filtered.filter((a) => {
        const attrs = getProductAttributes(a);
        const year =
          attrs.year_of_manufacture ||
          attrs.construction_year_of_manufacture ||
          attrs.packaging_year_of_manufacture ||
          attrs.energy_year_of_manufacture;
        return String(year) === String(applied.year);
      });
    }
    if (applied.fuelType) {
      filtered = filtered.filter((a) => {
        const attrs = getProductAttributes(a);
        const fuelType = attrs.fuel_ev_type || attrs.construction_fuel_type || attrs.fuel_type;
        return fuelType === applied.fuelType;
      });
    }
    if (applied.transmission) {
      filtered = filtered.filter((a) => getProductAttributes(a).transmission === applied.transmission);
    }
    if (applied.endCondition) {
      filtered = filtered.filter((a) => (a.auction_end_condition || "") === applied.endCondition);
    }
    if (applied.minPrice) {
      const min = parseFloat(applied.minPrice);
      if (!Number.isNaN(min)) {
        filtered = filtered.filter((a) => getBuyerStartingPrice(a) >= min);
      }
    }
    if (applied.maxPrice) {
      const max = parseFloat(applied.maxPrice);
      if (!Number.isNaN(max)) {
        filtered = filtered.filter((a) => getBuyerStartingPrice(a) <= max);
      }
    }

    return sortAuctions(filtered, applied.sort);
  }, [allAuctions, applied, matchingCategoryIds, matchingSubCategoryIds]);

  const totalPages = filteredAuctions.length > 0 ? Math.ceil(filteredAuctions.length / pageSize) : 0;
  const paginatedAuctions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAuctions.slice(start, start + pageSize);
  }, [filteredAuctions, currentPage, pageSize]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push("…");
    for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p += 1) {
      pages.push(p);
    }
    if (currentPage < totalPages - 2) pages.push("…");
    pages.push(totalPages);
    return pages;
  }, [totalPages, currentPage]);

  const patchFilters = (updates) => {
    setSearchParams(buildSearchParams(searchParams, { ...updates, page: "" }));
  };

  const applyFilters = () => {
    setSearchParams(
      buildSearchParams(searchParams, {
        category: draft.category,
        subCategory: draft.subCategory,
        minPrice: draft.minPrice,
        maxPrice: draft.maxPrice,
        brand: draft.brand,
        country: draft.country,
        condition: draft.condition,
        year: draft.year,
        fuelType: draft.fuelType,
        transmission: draft.transmission,
        endCondition: draft.endCondition,
        sort: draft.sort,
        q: draft.q,
        page: "",
      }),
    );
  };

  const clearFilters = () => {
    setSearchParams(
      buildSearchParams(searchParams, {
        minPrice: "",
        maxPrice: "",
        brand: "",
        country: "",
        condition: "",
        year: "",
        fuelType: "",
        transmission: "",
        endCondition: "",
        q: "",
        sort: "ending_soon",
        page: "",
      }),
    );
  };

  const goToPage = (page) => {
    setSearchParams(buildSearchParams(searchParams, { page: String(page) }));
  };

  const categoryOptions = categoryTabItems.map((cat) => ({ value: cat.id, label: cat.name }));
  const subCategoryOptions = subCategoryTabItems.map((sub) => ({ value: sub.id, label: sub.name }));

  const toggleCompare = (auctionId) => {
    const id = Number(auctionId);
    if (!Number.isFinite(id) || id <= 0) return;

    setCompareIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        setCompareAuctionsById((map) => {
          const nextMap = { ...map };
          delete nextMap[id];
          return nextMap;
        });
        if (next.length < 2) setCompareModalOpen(false);
        return next;
      }

      if (prev.length >= MAX_COMPARE_AUCTIONS) {
        toast.info(`You can compare up to ${MAX_COMPARE_AUCTIONS} auctions at a time.`);
        return prev;
      }

      const auction = filteredAuctions.find((item) => Number(item.auction_id) === id);
      if (auction) {
        setCompareAuctionsById((map) => ({ ...map, [id]: auction }));
      }

      return [...prev, id];
    });
  };

  const compareAuctions = useMemo(
    () => compareIds.map((id) => compareAuctionsById[id]).filter(Boolean),
    [compareIds, compareAuctionsById],
  );

  if (!showListings) {
    return null;
  }

  return (
    <>
      <div className="product-marketplace-page product-marketplace-page--explore product-marketplace-page--subcategory-products">
        <B2BHubHomeHeader />
        <ExploreAuctionListingsBrowse
          parentCategory={activeCategory}
          subCategory={activeSubCategory}
          auctions={paginatedAuctions}
          loading={loading || browseLoading}
          applied={applied}
          draft={draft}
          setDraft={setDraft}
          filteredTotal={filteredAuctions.length}
          filterOptions={filterOptions}
          categoryOptions={categoryOptions}
          subCategoryOptions={subCategoryOptions}
          onSearchSubmit={(q) => patchFilters({ q: q || "" })}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
          onSortChange={(sort) => patchFilters({ sort })}
          onGoToPage={goToPage}
          onChangePageSize={(limit) =>
            setSearchParams(buildSearchParams(searchParams, { limit: String(limit), page: "1" }))
          }
          pageSize={pageSize}
          pageNumbers={pageNumbers}
          totalPages={totalPages}
          compareIds={compareIds}
          onToggleCompare={toggleCompare}
        />
      </div>
      <HubFloatingRail />

      {compareIds.length > 0 ? (
        <div className="marketplace-compare-bar">
          <div className="marketplace-compare-bar__inner">
            <p>
              {compareIds.length} auction{compareIds.length === 1 ? "" : "s"} selected
              {compareIds.length < MAX_COMPARE_AUCTIONS
                ? ` — add up to ${MAX_COMPARE_AUCTIONS - compareIds.length} more`
                : " — maximum reached"}
            </p>
            <div className="marketplace-compare-bar__actions">
              <button
                type="button"
                className="marketplace-compare-bar__clear"
                onClick={() => {
                  setCompareIds([]);
                  setCompareAuctionsById({});
                  setCompareModalOpen(false);
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className="marketplace-compare-bar__view"
                disabled={compareIds.length < 2}
                onClick={() => setCompareModalOpen(true)}
              >
                <FaBalanceScale aria-hidden /> View Comparison
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AuctionComparisonModal
        open={compareModalOpen}
        auctions={compareAuctions}
        onClose={() => setCompareModalOpen(false)}
      />
    </>
  );
}
